import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';


const NotificationPreferencesTab = ({ notificationSettings, onSave }) => {
  const [settings, setSettings] = useState({
    emailNotifications: notificationSettings?.emailNotifications || true,
    pushNotifications: notificationSettings?.pushNotifications || true,
    smsNotifications: notificationSettings?.smsNotifications || false,
    newMessages: notificationSettings?.newMessages || true,
    newMatches: notificationSettings?.newMatches || true,
    profileViews: notificationSettings?.profileViews || true,
    subscriptionReminders: notificationSettings?.subscriptionReminders || true,
    promotionalEmails: notificationSettings?.promotionalEmails || false,
    weeklyDigest: notificationSettings?.weeklyDigest || true,
    emailFrequency: notificationSettings?.emailFrequency || 'instant',
    quietHoursEnabled: notificationSettings?.quietHoursEnabled || false,
    quietHoursStart: notificationSettings?.quietHoursStart || '22:00',
    quietHoursEnd: notificationSettings?.quietHoursEnd || '08:00'
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const emailFrequencyOptions = [
    { value: 'instant', label: 'Instant', description: 'Get notified immediately' },
    { value: 'hourly', label: 'Hourly', description: 'Digest every hour' },
    { value: 'daily', label: 'Daily', description: 'Once per day summary' },
    { value: 'weekly', label: 'Weekly', description: 'Weekly summary' }
  ];

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      onSave(settings);
      setIsSaving(false);
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    }, 1500);
  };

  const handleDisableAll = () => {
    setSettings({
      ...settings,
      emailNotifications: false,
      pushNotifications: false,
      smsNotifications: false,
      newMessages: false,
      newMatches: false,
      profileViews: false,
      subscriptionReminders: false,
      promotionalEmails: false,
      weeklyDigest: false
    });
  };

  const handleEnableAll = () => {
    setSettings({
      ...settings,
      emailNotifications: true,
      pushNotifications: true,
      newMessages: true,
      newMatches: true,
      profileViews: true,
      subscriptionReminders: true,
      weeklyDigest: true
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">Notification Preferences</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage how you receive notifications</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDisableAll}
          >
            Disable All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEnableAll}
          >
            Enable All
          </Button>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-heading font-semibold mb-4">Notification Channels</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <div className="p-2 bg-card rounded-lg">
                <Icon name="Mail" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <Checkbox
                  label="Email Notifications"
                  description="Receive notifications via email"
                  checked={settings?.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e?.target?.checked)}
                />
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <div className="p-2 bg-card rounded-lg">
                <Icon name="Bell" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <Checkbox
                  label="Push Notifications"
                  description="Receive push notifications on your device"
                  checked={settings?.pushNotifications}
                  onChange={(e) => handleSettingChange('pushNotifications', e?.target?.checked)}
                />
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <div className="p-2 bg-card rounded-lg">
                <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <Checkbox
                  label="SMS Notifications"
                  description="Receive important alerts via SMS"
                  checked={settings?.smsNotifications}
                  onChange={(e) => handleSettingChange('smsNotifications', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Activity Notifications</h3>
          <div className="space-y-4">
            <Checkbox
              label="New Messages"
              description="Get notified when you receive new messages"
              checked={settings?.newMessages}
              onChange={(e) => handleSettingChange('newMessages', e?.target?.checked)}
            />

            <Checkbox
              label="New Matches"
              description="Get notified about new profile matches"
              checked={settings?.newMatches}
              onChange={(e) => handleSettingChange('newMatches', e?.target?.checked)}
            />

            <Checkbox
              label="Profile Views"
              description="Get notified when someone views your profile"
              checked={settings?.profileViews}
              onChange={(e) => handleSettingChange('profileViews', e?.target?.checked)}
            />

            <Checkbox
              label="Subscription Reminders"
              description="Receive reminders about subscription renewals"
              checked={settings?.subscriptionReminders}
              onChange={(e) => handleSettingChange('subscriptionReminders', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Marketing & Updates</h3>
          <div className="space-y-4">
            <Checkbox
              label="Promotional Emails"
              description="Receive special offers and promotions"
              checked={settings?.promotionalEmails}
              onChange={(e) => handleSettingChange('promotionalEmails', e?.target?.checked)}
            />

            <Checkbox
              label="Weekly Digest"
              description="Get a weekly summary of your activity"
              checked={settings?.weeklyDigest}
              onChange={(e) => handleSettingChange('weeklyDigest', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Email Frequency</h3>
          <Select
            label="How often do you want to receive email notifications?"
            options={emailFrequencyOptions}
            value={settings?.emailFrequency}
            onChange={(value) => handleSettingChange('emailFrequency', value)}
            description="Control the frequency of email notifications"
          />
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Quiet Hours</h3>
          <div className="space-y-4">
            <Checkbox
              label="Enable Quiet Hours"
              description="Pause notifications during specific hours"
              checked={settings?.quietHoursEnabled}
              onChange={(e) => handleSettingChange('quietHoursEnabled', e?.target?.checked)}
            />

            {settings?.quietHoursEnabled && (
              <div className="ml-6 pl-4 border-l-2 border-primary/20 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Start Time"
                    type="time"
                    value={settings?.quietHoursStart}
                    onChange={(e) => handleSettingChange('quietHoursStart', e?.target?.value)}
                  />
                  <Input
                    label="End Time"
                    type="time"
                    value={settings?.quietHoursEnd}
                    onChange={(e) => handleSettingChange('quietHoursEnd', e?.target?.value)}
                  />
                </div>
                <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg">
                  <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    You won't receive any notifications between {settings?.quietHoursStart} and {settings?.quietHoursEnd}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          fullWidth
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
        >
          Save Preferences
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="RotateCcw"
          iconPosition="left"
          onClick={() => setSettings({
            emailNotifications: notificationSettings?.emailNotifications || true,
            pushNotifications: notificationSettings?.pushNotifications || true,
            smsNotifications: notificationSettings?.smsNotifications || false,
            newMessages: notificationSettings?.newMessages || true,
            newMatches: notificationSettings?.newMatches || true,
            profileViews: notificationSettings?.profileViews || true,
            subscriptionReminders: notificationSettings?.subscriptionReminders || true,
            promotionalEmails: notificationSettings?.promotionalEmails || false,
            weeklyDigest: notificationSettings?.weeklyDigest || true,
            emailFrequency: notificationSettings?.emailFrequency || 'instant',
            quietHoursEnabled: notificationSettings?.quietHoursEnabled || false,
            quietHoursStart: notificationSettings?.quietHoursStart || '22:00',
            quietHoursEnd: notificationSettings?.quietHoursEnd || '08:00'
          })}
          disabled={isSaving}
        >
          Reset
        </Button>
      </div>
      {showConfirmation && (
        <div className="fixed bottom-4 right-4 z-[600] animate-slide-up">
          <div className="flex items-center gap-3 px-4 py-3 bg-success text-success-foreground rounded-lg shadow-warm-lg">
            <Icon name="CheckCircle" size={20} color="#FFFFFF" />
            <span className="text-sm font-medium">Notification preferences saved!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferencesTab;